import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { friendlyError } from '../utils/friendly-error';

/** Resumen de un borrador (listado; sin el estado completo). */
export interface SpaceDraftSummary {
  draft_id: number;
  business_id: number;
  title: string;
  current_step: string;
  progress: number;
  version: number;
  created_by_name: string | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
  /** Solo en el listado. */
  image_count?: number;
  cover_url?: string | null;
}

/** Foto de un borrador (ya guardada en el bucket). */
export interface SpaceDraftImage {
  image_id: number;
  file_name: string;
  url: string;
  sort_order: number;
}

export interface SpaceDraftFull<T = unknown> extends SpaceDraftSummary {
  data: T;
  images: SpaceDraftImage[];
}

export interface SpaceDraftPayload<T = unknown> {
  title: string;
  current_step: string;
  progress: number;
  data: T;
  version?: number;
}

/** Error normalizado: código del backend (DRAFT_VERSION_CONFLICT, DRAFT_NOT_FOUND...) + mensaje legible. */
export interface SpaceDraftError {
  status: number;
  code: string | null;
  message: string;
  draft?: SpaceDraftSummary | null;
}

/**
 * Borradores de espacios guardados en el servidor (compartidos por el
 * negocio): cualquier persona con permiso de crear espacios puede verlos y
 * continuarlos desde otro dispositivo.
 */
@Injectable({ providedIn: 'root' })
export class SpaceDraftsService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.apiUrl}/space-drafts`;

  private fail = (error: HttpErrorResponse): Observable<never> => throwError(() => ({
    status: error.status,
    code: error.error?.code ?? null,
    message: friendlyError(error, 'No se pudo completar la acción con el borrador.'),
    draft: error.error?.draft ?? null,
  } satisfies SpaceDraftError));

  list(businessId: number): Observable<{ drafts: SpaceDraftSummary[]; max: number }> {
    return this.http.get<{ drafts: SpaceDraftSummary[]; max: number }>(`${this.url}/business/${businessId}`).pipe(catchError(this.fail));
  }

  get<T>(draftId: number): Observable<SpaceDraftFull<T>> {
    return this.http.get<{ draft: SpaceDraftFull<T> }>(`${this.url}/${draftId}`).pipe(map(r => r.draft), catchError(this.fail));
  }

  create<T>(businessId: number, payload: SpaceDraftPayload<T>): Observable<SpaceDraftSummary> {
    return this.http.post<{ draft: SpaceDraftSummary }>(`${this.url}/business/${businessId}`, payload).pipe(map(r => r.draft), catchError(this.fail));
  }

  update<T>(draftId: number, payload: SpaceDraftPayload<T> & { version: number }): Observable<SpaceDraftSummary> {
    return this.http.put<{ draft: SpaceDraftSummary }>(`${this.url}/${draftId}`, payload).pipe(map(r => r.draft), catchError(this.fail));
  }

  /** Sube fotos directo al bucket (WebP) y las asocia al borrador. */
  uploadImages(draftId: number, files: File[]): Observable<SpaceDraftImage[]> {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file, file.name));
    return this.http.post<{ images: SpaceDraftImage[] }>(`${this.url}/${draftId}/images`, formData).pipe(map(r => r.images), catchError(this.fail));
  }

  /** Quita la foto del borrador y la borra del bucket por completo. */
  removeImage(draftId: number, imageId: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/${draftId}/images/${imageId}`).pipe(catchError(this.fail));
  }

  /** Al crear el espacio: las fotos del borrador pasan al espacio y el borrador se elimina. */
  adopt(draftId: number, spaceId: number): Observable<{ images_adopted: number }> {
    return this.http.post<{ images_adopted: number }>(`${this.url}/${draftId}/adopt`, { space_id: spaceId }).pipe(catchError(this.fail));
  }

  remove(draftId: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/${draftId}`).pipe(catchError(this.fail));
  }
}
